import * as React from "react";
import { useCallback, useEffect, useMemo } from "react";
import { styled, Theme } from "@mui/material";
import {
  LocationQuestionsMetaData,
  Orientation,
} from "./locationQuestionsIndex";
import { GenericButton } from "../../../uiControllers/genericButton";
import { useTranslate } from "../../../../hooks/useTranslate";
import { PatientColorMap } from "../../../../layout/patientColorMap";
import {
  DesktopBreakpoint,
  TabletBreakpoint,
} from "../../../../layout/breakpoints";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import { SvgLoader, SvgProxy } from "react-svgmt";
import { HELIX_CLINIC_ID } from "../../../../hooks/usePartnerTheme";
import { useClinicData } from "../../../../clinic/clinicHooks";

//region [[ Styles ]]

type StyleProps = {
  selected: string[];
  supported: string[];
  prefix: string;
};

const useStyles = makeStyles<Theme, StyleProps>((theme: Theme) => ({
  locationMapView: ({ selected, supported, prefix }: StyleProps) => {
    const selectedSelector = "& .selected:not([stroke])";

    const selectedStrokeSelector = "& .selected[stroke]";

    const supportedSelectorHelper = (tag) => `& .supported${tag}`;

    const allSupportedSelector = supportedSelectorHelper("");
    const supportedFillSelector = supportedSelectorHelper(":not([stroke])");
    const supportedHoverFillSelector = supportedSelectorHelper(
      ":not(& [stroke]):hover",
    );
    const supportedStrokeSelector = supportedSelectorHelper("[stroke]");
    const supportedHoverStrokeSelector = supportedSelectorHelper(
      "[stroke]:hover",
    );

    return {
      width: "100%",
      [theme.breakpoints.up(DesktopBreakpoint)]: {
        // Special size for embedded within desktop
        ".main-app.embedded & .diagram, .main-app.embedded & .injected-svg": {
          // height: "calc(100vh - 455px)",
          // width: "min(100vw - 5px, 786px)",
        },
        // very special hack for helix
        ".elementor-widget-container .main-app.embedded .helix & .diagram, .main-app.embedded .helix & .injected-svg": {
          height: "calc(100vh - 531px)",
          width: "min(100vw - 5px, 799px)",
        },
      },
      "& svg": { width: "100%" },
      "& .diagram, & .injected-svg": {
        // height: "calc(100vh - 315px)",
      },
      // ".inline & .diagram, .inline & .injected-svg": {
      //   // height: "auto",
      //   // width: "min(calc(100vw - 5px), 595px)",
      //   // margin: " 0 -38px",
      // },
      "& [data-feature-id]": {
        fill: "none",
        stroke: "none",
      },
      [`${allSupportedSelector}`]: {
        cursor: "pointer",
        fill: PatientColorMap.OrganMapBackground,
      },
      [`${supportedFillSelector}`]: {
        fill: `${PatientColorMap.OrganMapBackground}`,
      },
      [`${supportedStrokeSelector}`]: {
        stroke: PatientColorMap.OrganMapBackground,
      },
      [`${selectedSelector}`]: {
        fill: PatientColorMap.Primary,
      },
      [theme.breakpoints.up(TabletBreakpoint)]: {
        [`${supportedHoverFillSelector}, ${selectedSelector}`]: {
          fill: PatientColorMap.Primary,
        },
      },
      [`${supportedHoverStrokeSelector}, ${selectedStrokeSelector}`]: {
        stroke: PatientColorMap.Primary,
      },
      "& [disabled='true']": {
        fill: "#FFF",
      },
      "& [stroke-patient-color='primary']": {
        stroke: PatientColorMap.Primary,
      },
      "& [fill-patient-color='primary']": {
        fill: PatientColorMap.Primary,
      },
      "& [stroke-patient-color='background']": {
        stroke: PatientColorMap.OrganMapBackground,
      },
      "& [fill-patient-color='background']": {
        fill: PatientColorMap.OrganMapBackground,
      },
    };
  },
}));

const SelectAllButton = styled(GenericButton)({
  "&.generic-button": {
    "&, &.clicked": {
      background: PatientColorMap.DefaultLight,
      color: PatientColorMap.Default,
    },
    "&.selected,&.clicked.selected": {
      background: PatientColorMap.Primary,
      color: "#FFF",
    },
  },
});

type AllOverContainerProps = { theme?: Theme; cstyle: any };
const AllOverContainer = styled("div")(
  ({ theme, cstyle }: AllOverContainerProps) => ({
    position: "absolute",
    left: "5%",
    bottom: "15px",
    cursor: "pointer",
    display: "flex",
    justifyContent: "flex-start",
    width: "25%",
    "& svg": {
      cursor: "pointer",
      maxHeight: "10vh", // Don't let the image get over 10% of the view port
    },
    ...cstyle,
  }),
);

const MapWithOrientationContainer = styled("div")({
  display: "flex",
  flexFlow: "row",
  alignItems: "stretch",
  justifyContent: "center",
  direction: "ltr",
  position: "relative",
});

const OrientationArea = styled("div")({
  display: "flex",
  flexFlow: "column",
  justifyContent: "flex-start",
  position: "absolute",
  "&.L": {
    right: 0,
  },
  "&.R": {
    left: 0,
  },
});

const OrientationIcon = styled("div")({
  display: "flex",
  flexFlow: "row",
  justifyContent: "center",
  alignItems: "center",
  padding: "7px",
  overflow: "hidden",
  // border: `1px solid ${PatientColorMap.OrganMapBackground}`,
  borderRadius: "50%",
  color: PatientColorMap.Primary,
  fontSize: "11px",
  lineHeight: "11px",
  fontWeight: 700,
  textAlign: "center",
  width: "35px",
  height: "35px",
});

//endregion [[ Styles ]]

//region [[ Props ]]

export interface LocationMapProps {
  className?: string;
  selectedIds: string[];
  onSelect: (id: string) => void;
  locationQuestion: LocationQuestionsMetaData;
  /* list of ids the user can select */
  supportedFeatures?: string[];
}

//endregion [[ Props ]]

//region [[ Functions ]]
//endregion [[ Functions ]]

export const LocationMap = ({
  onSelect,
  locationQuestion,
  selectedIds,
  supportedFeatures,
  ...props
}: LocationMapProps) => {
  const { translate } = useTranslate();

  const notYetSelected = useMemo(
    () =>
      supportedFeatures.filter(
        (supported) =>
          selectedIds.findIndex((selected) => selected === supported) < 0,
      ),
    [supportedFeatures, selectedIds],
  );

  const allOverSettings = locationQuestion?.allOverSettings;

  const applyToAllOrganIconsWithFeatureId = useCallback(
    (fId: string, callback) => {
      const key = `[data-feature-id='${fId}']`;
      const organNodes = document.querySelectorAll(key);
      if (!organNodes || !organNodes?.length) return;
      organNodes?.forEach(callback);
    },
    [],
  );

  const toggleAllOver = useCallback(() => {
    // If we're supposed to send only one feature, send it
    if (allOverSettings.allOverFeatureId) {
      // If there are other areas selected clear them
      const selectedAreas = selectedIds.filter(
        (id) => id !== allOverSettings.allOverFeatureId,
      );
      if (selectedAreas.length) {
        selectedAreas.forEach((fId) => {
          onSelect(fId);
          applyToAllOrganIconsWithFeatureId(fId, (featureIcon) =>
            featureIcon?.classList.remove("selected"),
          );
        });
      }
      // Select the all over feature
      onSelect(allOverSettings.allOverFeatureId);
      return;
    }
    // Otherwise figure out which features were not yet selected and mark them
    if (
      !notYetSelected.length ||
      notYetSelected.length === supportedFeatures.length
    ) {
      // If all were selected, or none were selected - mark all as selected
      supportedFeatures.forEach((supportedFeature) =>
        onSelect(supportedFeature),
      );
      return;
    }
    // Mark those not yet selected as selected
    notYetSelected?.forEach((notYetSelected) => onSelect(notYetSelected));
  }, [
    allOverSettings,
    notYetSelected,
    selectedIds,
    onSelect,
    supportedFeatures,
    applyToAllOrganIconsWithFeatureId,
  ]);

  const markAllOverAsChecked = useMemo(
    () =>
      // !notYetSelected?.length ||
      selectedIds.some((id) => id === allOverSettings?.allOverFeatureId),
    [selectedIds, allOverSettings],
  );

  const handleMapClick = useCallback(
    (e) => {
      if (e.target && e.target.attributes["data-feature-id"]) {
        const value = e?.target?.attributes["data-feature-id"]?.value;
        applyToAllOrganIconsWithFeatureId(value, (icon) => {
          if (icon.classList.contains("selected")) {
            icon.classList.remove("selected");
          }
        });
        onSelect(value);
        // Clear all over if one is selected
        if (markAllOverAsChecked) toggleAllOver();
      }
    },
    [
      onSelect,
      markAllOverAsChecked,
      toggleAllOver,
      applyToAllOrganIconsWithFeatureId,
    ],
  );

  const renderAllOver = useCallback(() => {
    if (
      !allOverSettings?.allOverCheckedIcon ||
      !allOverSettings.allOverUnCheckedIcon
    ) {
      // Currently we don't really support generic buttons to toggle all areas
      // but we can just render it for fun
      return (
        <SelectAllButton
          className={markAllOverAsChecked ? "selected" : ""}
          onClick={toggleAllOver}
          label={translate(allOverSettings.allOverText || "ALL_OVER")}
        />
      );
    }
    if (markAllOverAsChecked) {
      return <allOverSettings.allOverCheckedIcon onClick={toggleAllOver} />;
    }
    return <allOverSettings.allOverUnCheckedIcon onClick={toggleAllOver} />;
  }, [allOverSettings, markAllOverAsChecked, toggleAllOver, translate]);

  const handleSvgRendered = useCallback(() => {
    supportedFeatures.forEach((fId) => {
      applyToAllOrganIconsWithFeatureId(fId, (organIcon) =>
        // Every time the list of supported ids is changed, make all svg paths
        // with those feature ids hold the "supported" class name so it would appear like a
        // clickable button
        organIcon?.classList.add("supported"),
      );
    });
    // Use efect to mark selected ids so if the map reloads with list of
    // selected ids they will still be re-marked as selected as we have
    // rash where we can move between back and front
    selectedIds.forEach((fId) => {
      applyToAllOrganIconsWithFeatureId(fId, (organIcon) =>
        organIcon?.classList.add("selected"),
      );
    });
  }, [supportedFeatures, selectedIds, applyToAllOrganIconsWithFeatureId]);

  useEffect(() => {
    handleSvgRendered();
  }, [handleSvgRendered, selectedIds]);

  const classes = useStyles({
    selected: selectedIds,
    supported: supportedFeatures,
    prefix: "",
  });

  const L = "LEFT_AREA_INDICATOR",
    R = "RIGHT_AREA_INDICATOR";

  const isOrientationReversed =
    locationQuestion.orientation === Orientation.Reverse;

  const { clinic } = useClinicData();

  const isHelix = clinic === HELIX_CLINIC_ID;

  return (
    <MapWithOrientationContainer>
      {locationQuestion.orientation !== Orientation.Hidden && (
        <OrientationArea className={"R"}>
          <OrientationIcon>
            {translate(isOrientationReversed ? L : R)}
          </OrientationIcon>
        </OrientationArea>
      )}
      <div
        className={clsx(
          props.className,
          classes.locationMapView,
          isHelix ? "helix" : "",
        )}
      >
        <SvgLoader
          path={locationQuestion.component}
          onSVGReady={handleSvgRendered}
          onClick={handleMapClick}
        >
          <SvgProxy selector={"path[data-feature-id]"} />
        </SvgLoader>
        {allOverSettings?.enabled &&
          /* Show all over only if supported */
          (!allOverSettings.allOverFeatureId ||
            supportedFeatures.find(
              (fId) => fId === allOverSettings.allOverFeatureId,
            )) && (
            <AllOverContainer cstyle={allOverSettings.customContainerStyle}>
              {renderAllOver()}
            </AllOverContainer>
          )}
      </div>
      {locationQuestion.orientation !== Orientation.Hidden && (
        <OrientationArea className={"L"}>
          <OrientationIcon>
            {translate(isOrientationReversed ? R : L)}
          </OrientationIcon>
        </OrientationArea>
      )}
    </MapWithOrientationContainer>
  );
};
